/**
 * ServisEmailWorkspace — the company's identity in the servis customer emails
 * (order confirmation + 48 h reminder): team name (signature + sender name),
 * footer phone, contact e-mail (footer + reply-to + CC). Spravce-gated
 * server-side (`/v1/firma/servis-email`); changes land in Historie.
 *
 * A company sends these emails only once name + contact e-mail are set.
 */

import { useEffect, useState } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Field, Fields } from '@/components/Field';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useFirmaServisEmailQuery, useUpdateFirmaServisEmailMutation } from './api';

interface ServisEmailWorkspaceProps {
  canManage: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ServisEmailWorkspace({ canManage }: ServisEmailWorkspaceProps) {
  const query = useFirmaServisEmailQuery(canManage);
  const save = useUpdateFirmaServisEmailMutation();
  const [nazev, setNazev] = useState('');
  const [telefon, setTelefon] = useState('');
  const [kontakt, setKontakt] = useState('');

  useEffect(() => {
    if (!query.data) return;
    setNazev(query.data.nazev);
    setTelefon(query.data.telefon);
    setKontakt(query.data.kontakt);
  }, [query.data]);

  if (!canManage) {
    return (
      <div className="flex min-h-0 flex-1 flex-col px-8 pb-8 pt-5">
        <EmptyState
          eyebrow="403"
          title="Pouze pro správce"
          description="E-maily servisu nastavují správci firmy a administrátoři."
        />
      </div>
    );
  }

  const kontaktTrim = kontakt.trim();
  const kontaktInvalid = kontaktTrim !== '' && !EMAIL_RE.test(kontaktTrim);
  const aktivni = nazev.trim() !== '' && kontaktTrim !== '' && !kontaktInvalid;
  const dirty =
    query.data != null &&
    (nazev.trim() !== query.data.nazev ||
      telefon.trim() !== query.data.telefon ||
      kontaktTrim.toLowerCase() !== query.data.kontakt);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (kontaktInvalid) return;
    try {
      await save.mutateAsync({ nazev, telefon, kontakt });
      toast.ok('Nastavení uloženo');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Uložení se nezdařilo');
    }
  };

  return (
    <div
      data-testid="servis-email-workspace"
      className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5"
    >
      {query.isLoading ? (
        <Skeleton className="h-64 w-full max-w-3xl" />
      ) : (
        <div className="grid max-w-5xl gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              {query.data?.aktivni ? (
                <Badge tone="ok">E-maily zapnuty</Badge>
              ) : (
                <Badge tone="neutral">E-maily vypnuty</Badge>
              )}
              <span className="text-[12.5px] text-ink-3">
                {query.data?.aktivni
                  ? 'Zákazníci dostávají potvrzení a připomínky termínů.'
                  : 'Vyplňte název týmu a kontaktní e-mail — do té doby se zákazníkům nic neodesílá.'}
              </span>
            </div>
            <Fields cols={1}>
              <Field
                label="Název týmu"
                required
                hint="Podpis „tým …“ a jméno odesílatele „Servis …“. Např. Auto Trutnov."
              >
                <TextInput
                  data-testid="servis-email-nazev"
                  maxLength={100}
                  value={nazev}
                  onChange={(e) => setNazev(e.currentTarget.value)}
                />
              </Field>
              <Field label="Telefon" optional="volitelné" hint="Zobrazí se v patičce e-mailu.">
                <TextInput
                  data-testid="servis-email-telefon"
                  maxLength={50}
                  value={telefon}
                  onChange={(e) => setTelefon(e.currentTarget.value)}
                />
              </Field>
              <Field
                label="Kontaktní e-mail"
                required
                state={kontaktInvalid ? 'error' : 'default'}
                hint={
                  kontaktInvalid
                    ? 'Neplatný e-mail'
                    : 'V patičce, jako adresa pro odpovědi a jako kopie každého e-mailu.'
                }
                hintTone={kontaktInvalid ? 'error' : 'neutral'}
              >
                <TextInput
                  data-testid="servis-email-kontakt"
                  type="email"
                  maxLength={255}
                  value={kontakt}
                  onChange={(e) => setKontakt(e.currentTarget.value)}
                />
              </Field>
            </Fields>
            <div className="flex justify-end gap-2">
              <Button
                type="submit"
                variant="ink"
                data-testid="servis-email-save"
                disabled={!dirty || kontaktInvalid || save.isPending}
              >
                {save.isPending ? 'Ukládám…' : 'Uložit'}
              </Button>
            </div>
          </form>

          <aside className="flex flex-col gap-2" aria-label="Náhled patičky">
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              Náhled patičky e-mailu
            </span>
            <div data-testid="servis-email-preview" className="border border-line bg-paper p-5 text-[13px] leading-relaxed text-ink-2">
              <p className="text-ink-3">…</p>
              <p className="mt-3">
                Děkujeme a budeme se těšit,
                <br />
                tým {nazev.trim() || '…'}
              </p>
              <p className="mt-3">
                {telefon.trim() && (
                  <>
                    Telefon: {telefon.trim()}
                    <br />
                  </>
                )}
                E-mail: {kontaktTrim || '…'}
              </p>
              <p className="mt-4 border-t border-line pt-3 font-mono text-[10.5px] uppercase tracking-[0.06em] text-ink-3">
                Odesílatel: Servis {nazev.trim() || '…'}
                {!aktivni && ' · e-maily se neodesílají'}
              </p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
